import React, { useState, useEffect, createRef } from 'react';
import { styled } from '@compiled/react';
import Lozenge from '@atlaskit/lozenge';
import { Header, Section, SkeletonItem } from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';
import { isFedRamp } from '@atlaskit/atlassian-context';
import { CONNECT_ROUTES_SET } from '@atlassian/jira-common-constants/src/spa-routes-sets.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { PROJECT_SETTINGS_PAGE_MODULE } from '@atlassian/jira-forge-ui-constants/src/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { EngagementSpotlight } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/engagement-spotlight/main.tsx';
import { ExpandableSubmenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/expandable-submenu-item/index.tsx';
import { FireUiAnalytics } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/fire-ui-analytics/index.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { ExpandableProjectSettingsWrapper } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/navigation-component-wrappers/index.tsx';
import { SpotlightTarget } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/spotlight-target/index.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { NotificationsSubMenu } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/notifications-sub-menu/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { matchUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import EcosystemMenu from '@atlassian/jira-navigation-apps-sidebar-ecosystem/src/ui/menu/index.tsx';
import { useDlpEntitlement } from '@atlassian/jira-tenant-context-controller/src/components/dlp-entitlement/index.tsx';
import { useRouter, type JiraSpaRoute } from '@atlassian/react-resource-router';
import type { RouteName } from '@atlassian/jira-route-name-types/src/RouteName.tsx';
import { useSettings } from '../../../controllers/settings/index.tsx';
import { messages } from './messages.tsx';
import { ToolchainMenuItem } from './toolchain-menu-item/index.tsx';
import type { SettingsProps } from './types.tsx';

const useThrowProject = withThrowOnError(useProject);
const useThrowSettings = withThrowOnError(useSettings);

const ISSUES_ROUTE_NAMES: RouteName[] = [
	'legacy-project-settings-issue-types',
	'legacy-project-settings-issue-layout',
	'legacy-project-settings-screens',
	'legacy-project-settings-fields',
	'legacy-project-settings-collectors',
	'legacy-project-settings-issue-security',
];

const APPS_ROUTES: RouteName[] = [
	'project-settings-software-forge-apps',
	...CONNECT_ROUTES_SET.values(),
];

export const calculateIssuesMenuOpen = (routeName: RouteName) =>
	ISSUES_ROUTE_NAMES.includes(routeName);

const calculateAppsMenuOpen = (routeName: RouteName) => APPS_ROUTES.includes(routeName);

const calculateNotificationsActive = ({ name }: JiraSpaRoute) =>
	name === 'classic-software-notifications-issue-logs';

export const Settings = ({ ecosystemItems }: SettingsProps) => {
	const { formatMessage } = useIntl();
	const { data: project } = useThrowProject();
	const { data: features } = useThrowSettings();
	const [{ route }] = useRouter();
	const hasDlpEnabled = useDlpEntitlement();
	const baseProjectConfigUrl = `/plugins/servlet/project-config/${project?.key ?? ''}`;

	// We need a way to tell a neseted menu item within a expandable section that it is currently
	// expanded on order to get the sidbar scrolling to work correctly
	const [issuesMenuItem, setIssuesMenuItem] = useState<boolean>(
		calculateIssuesMenuOpen(route.name),
	);

	const [appsMenuOpen, setAppsMenuOpen] = useState<boolean>(calculateAppsMenuOpen(route.name));
	const [notificationsActive] = useState<boolean>(calculateNotificationsActive(route));

	useEffect(() => {
		setIssuesMenuItem(calculateIssuesMenuOpen(route.name));
		setAppsMenuOpen(calculateAppsMenuOpen(route.name));
	}, [route]);

	if (features == null || project == null) return <SkeletonItem />;

	const BackMenuItem = {
		href: `/plugins/servlet/project-config/${project.key}`,
		title: formatMessage(messages.backItem),
	};

	const defaultIssuesSettingsSection = (
		<ExpandableSubmenuItem
			analytics={{ itemId: 'issues' }}
			menuItemLabel={formatMessage(
				fg('jira-issue-terminology-refresh-m3') ? messages.issuesIssueTermRefresh : messages.issues,
			)}
			forceExpand={issuesMenuItem}
			submenuId="classic-issues-setttings"
			testId="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-issues"
		>
			{features.hasIssueTypes && (
				<MenuItem
					analytics={{ itemId: 'issue-types' }}
					selectedOn={matchUrlPathname()}
					href={`${baseProjectConfigUrl}/issuetypes`}
					ref={createRef()}
					data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-issue-types"
				>
					{formatMessage(messages.types)}
				</MenuItem>
			)}
			{features.hasIssueLayout && (
				<MenuItem
					analytics={{ itemId: 'issue-layout' }}
					selectedOn={matchUrlPathname()}
					href={`${baseProjectConfigUrl}/issuelayout`}
					ref={createRef()}
					data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-issue-layout"
				>
					{formatMessage(messages.layout)}
				</MenuItem>
			)}
			{features.hasScreens && (
				<MenuItem
					analytics={{ itemId: 'screens' }}
					selectedOn={matchUrlPathname()}
					href={`${baseProjectConfigUrl}/screens`}
					ref={createRef()}
					data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-screens"
				>
					{formatMessage(messages.screens)}
				</MenuItem>
			)}
			{features.hasFields && (
				<MenuItem
					analytics={{ itemId: 'fields' }}
					selectedOn={matchUrlPathname()}
					href={`${baseProjectConfigUrl}/fields`}
					ref={createRef()}
					data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-fields"
				>
					{formatMessage(messages.fields)}
				</MenuItem>
			)}
			{fg('proforma_mvp_jsw_project_settings') && (
				<MenuItem
					analytics={{ itemId: 'proforma-forms' }}
					href={`/jira/software/c/projects/${project.key}/settings/forms`}
					selectedOn={['project-settings-software-proforma-project-forms']}
					ref={createRef()}
					data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-proforma-fields"
				>
					{expVal('forms_for_jsw_experiment', 'isEnabled', false)
						? formatMessage(messages.advancedForms)
						: formatMessage(messages.forms)}
				</MenuItem>
			)}
			{features.hasIssueCollectors && (
				<MenuItem
					analytics={{ itemId: 'issue-collectors' }}
					selectedOn={['legacy-project-settings-collectors']}
					href={`/secure/ViewCollectors!default.jspa?projectKey=${project.key}`}
					ref={createRef()}
					data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-issue-collectors"
				>
					{formatMessage(messages.collectors)}
				</MenuItem>
			)}
			{features.hasIssueSecurity && (
				<MenuItem
					analytics={{ itemId: 'issue-security' }}
					selectedOn={matchUrlPathname()}
					href={`${baseProjectConfigUrl}/issuesecurity`}
					ref={createRef()}
					data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-issue-security"
				>
					{formatMessage(messages.security)}
				</MenuItem>
			)}
		</ExpandableSubmenuItem>
	);

	const nonAdminIssueLayoutSection = (
		<ExpandableSubmenuItem
			analytics={{ itemId: 'issues' }}
			menuItemLabel={formatMessage(
				fg('jira-issue-terminology-refresh-m3') ? messages.issuesIssueTermRefresh : messages.issues,
			)}
			forceExpand={issuesMenuItem}
			submenuId="classic-issues-setttings"
			testId="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-issues"
		>
			<MenuItem
				analytics={{ itemId: 'issue-layout' }}
				selectedOn={matchUrlPathname()}
				href={`${baseProjectConfigUrl}/issuelayout`}
				ref={createRef()}
				data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-issue-layout"
			>
				{formatMessage(messages.layout)}
			</MenuItem>
		</ExpandableSubmenuItem>
	);

	const issuesSection = (() => {
		if (features.hasIssuesDropdown) {
			return defaultIssuesSettingsSection;
		}
		if (!features.hasIssuesDropdown && features.hasIssueLayout) {
			return nonAdminIssueLayoutSection;
		}
	})();

	return (
		<ExpandableProjectSettingsWrapper>
			<Section>
				<ProjectSettingsLunarHeadingWrapper>
					<Header>{formatMessage(messages.settings)}</Header>
				</ProjectSettingsLunarHeadingWrapper>
				{features.hasDetails && (
					<MenuItem
						analytics={{ itemId: 'details' }}
						selectedOn={matchUrlPathname()}
						href={`/jira/software/c/projects/${project.key}/settings/details`}
						ref={createRef()}
						data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-details"
					>
						{formatMessage(messages.details)}
					</MenuItem>
				)}
				{features.hasSummary && (
					<MenuItem
						analytics={{ itemId: 'summary' }}
						selectedOn={matchUrlPathname()}
						href={`${baseProjectConfigUrl}/summary`}
						ref={createRef()}
						data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-summary"
					>
						{formatMessage(messages.summary)}
					</MenuItem>
				)}
				{features.hasPeople && (
					<MenuItem
						analytics={{ itemId: 'people' }}
						selectedOn={matchUrlPathname()}
						href={`${baseProjectConfigUrl}/people`}
						ref={createRef()}
						data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-people"
					>
						{formatMessage(messages.people)}
					</MenuItem>
				)}
				{features.hasPermissions && (
					<MenuItem
						analytics={{ itemId: 'permissions' }}
						selectedOn={matchUrlPathname()}
						href={`${baseProjectConfigUrl}/permissions`}
						ref={createRef()}
						data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-permissions"
					>
						{formatMessage(messages.permissions)}
					</MenuItem>
				)}
				{features.hasNotifications && (
					<ExpandableSubmenuItem
						analytics={{ itemId: 'notifications' }}
						menuItemLabel={formatMessage(messages.notifications)}
						forceExpand={notificationsActive}
						submenuId="classic-notifications-settings"
						testId="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-notifications"
					>
						<NotificationsSubMenu
							settingsUrl={`${baseProjectConfigUrl}/notifications`}
							issueLogsUrl={`/jira/software/c/projects/${project.key}/settings/notifications/issue-logs?projectId=${project.id}`}
						/>
					</ExpandableSubmenuItem>
				)}
				{features.hasAutomation && (
					<>
						<SpotlightTarget name="spotlight-sidebar-project-automation">
							<MenuItem
								analytics={{ itemId: 'automation' }}
								selectedOn={matchUrlPathname()}
								href={`/jira/software/c/projects/${project.key}/settings/automate`}
								ref={createRef()}
								data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-automation"
							>
								{formatMessage(messages.automation)}
							</MenuItem>
						</SpotlightTarget>
						<EngagementSpotlight engagementId="spotlight-sidebar-project-automation" />
						<FireUiAnalytics actionSubject="automationNavigationItem" action="viewed" />
					</>
				)}
				{features.hasFeatures && (
					<>
						<SpotlightTarget name="spotlight-sidebar-project-features">
							<MenuItem
								analytics={{ itemId: 'features' }}
								selectedOn={matchUrlPathname()}
								href={`/jira/software/c/projects/${project.key}/settings/features`}
								ref={createRef()}
								data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-features"
							>
								{formatMessage(messages.features)}
							</MenuItem>
						</SpotlightTarget>
						<EngagementSpotlight engagementId="spotlight-sidebar-project-features" />
						<FireUiAnalytics actionSubject="featuresNavigationItem" action="viewed" />
					</>
				)}

				{features.hasToolchains && (
					<ToolchainMenuItem projectId={project.id} projectKey={project.key} />
				)}

				{features.hasWorkflows && (
					<MenuItem
						analytics={{ itemId: 'workflows' }}
						selectedOn={matchUrlPathname()}
						href={`${baseProjectConfigUrl}/workflows`}
						ref={createRef()}
						data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-workflows"
					>
						{formatMessage(messages.workflows)}
					</MenuItem>
				)}

				{issuesSection}

				{features.hasComponents && (
					<MenuItem
						analytics={{ itemId: 'components' }}
						selectedOn={['project-settings-components']}
						href={`${baseProjectConfigUrl}/administer-components`}
						ref={createRef()}
						data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-components"
					>
						{formatMessage(messages.components)}
						<LozengeContainer>
							<Lozenge appearance="new">{formatMessage(messages.new)}</Lozenge>
						</LozengeContainer>
					</MenuItem>
				)}

				{features.hasApps && (
					<ExpandableSubmenuItem
						analytics={{ itemId: 'apps' }}
						menuItemLabel={formatMessage(messages.apps)}
						forceExpand={appsMenuOpen}
						submenuId="classic-apps-setttings"
						testId="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-apps"
					>
						<EcosystemMenu
							items={ecosystemItems}
							forgeModule={PROJECT_SETTINGS_PAGE_MODULE}
							overrides={{ BackMenuItem }}
						/>

						{!isFedRamp() && features.hasOpsgenie && (
							<MenuItem
								analytics={{ itemId: 'opsgenie' }}
								selectedOn={matchUrlPathname()}
								href={`/jira/software/c/projects/${project.key}/settings/opsgenie`}
								ref={createRef()}
								data-testid="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-opsgenie"
							>
								{formatMessage(messages.opsgenie)}
							</MenuItem>
						)}
					</ExpandableSubmenuItem>
				)}

				{features.hasDevtools && (
					<MenuItem
						selectedOn={matchUrlPathname()}
						analytics={{ itemId: 'devtools' }}
						href={`/secure/ConfigureDevStatus.jspa?projectKey=${project.key}`}
						ref={createRef()}
						data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-devtools"
					>
						{formatMessage(messages.devtools)}
					</MenuItem>
				)}

				{features.hasDeleteProject && (
					<MenuItem
						analytics={{ itemId: 'delete-project' }}
						selectedOn={['legacy-project-settings-delete']}
						href={`/secure/project/DeleteProject!default.jspa?pid=${project.id}&pcp=true`}
						ref={createRef()}
						data-test-id="navigation-apps-sidebar-software-classic.ui.menu.settings.menu-item-delete-project"
					>
						{formatMessage(messages.deleteProject)}
					</MenuItem>
				)}

				{hasDlpEnabled && (
					<MenuItem
						analytics={{ itemId: 'data-classification' }}
						href={`${baseProjectConfigUrl}/data-classifications`}
						selectedOn={['project-settings-classic-data-classifications']}
					>
						{formatMessage(messages.dataClassificationNonFinal)}
					</MenuItem>
				)}
			</Section>
		</ExpandableProjectSettingsWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const LozengeContainer = styled.span({
	marginLeft: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ProjectSettingsLunarHeadingWrapper = styled.div({
	paddingLeft: token('space.200'),
});
